import { useNavigate } from "react-router-dom";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import resouceGroupicon from "../../assets/task/resource_group.svg";
import resourceClock from "../../assets/task/resource_clock.svg";
import TeamMemberCard from "../../components/resourceOverview/TeamMemberCard";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  GET_RESOURCE_OVERVIEW,EMPLOYEE_ACTIVE_STATS_RESOURCES,
  GET_RESOURCE_WORKING_DATES,
  LEAVES_BY_POD,
  PODS_DATA_LIST,
} from "../../api/query";
import Loader from "../../components/common/Loader";
import { Controller, useForm } from "react-hook-form";
import DateSelector from "../../components/common/DateSelector";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  differenceInDays,
  isBefore,
  parseISO,
  format,
  isValid,
} from "date-fns";
import { showError } from "../../components/common/ToastNotification";
import { showSuccess } from "../../components/common/ToastNotification";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import InputsNew from "../../components/common/InputsNew";
import DynamicTable from "../../components/common/DynamicTable";
import { SortingState } from "@tanstack/react-table";
import { DELETE_RESOURCE_WORKING_DATES } from "../../api/mutation";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";

export function formatHoursToHM(value: any) {
  if (value == null || isNaN(value)) return "0H 0M";

  // Convert decimal hours into total minutes
  const totalMinutes = Math.round(value * 60);

  // Extract hours and minutes
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Build the string
  let result = "";
  if (hours > 0) result += `${hours}H `;
  if (minutes > 0) result += `${minutes}M`;

  return result.trim() || "0H 0M";
}

/**
 * ---- Employee Shift & Timing helpers --------------------------------------
 */

// react-hook-form's date value here is treated the same way the rest of this
// codebase treats it (parseISO-able string) — see the start_date/end_date
// Controllers below, which already assume that.
const toApiDateTime = (dateValue: string) => {
  if (!dateValue) return null;
  const d = parseISO(dateValue);
  return format(d, "yyyy-MM-dd'T'00:00:00");
};

// "10:00am" / "7:00pm" style, no space before am/pm.
const formatClockTime = (iso?: string | null) => {
  if (!iso) return "";

  const date = parseISO(iso);

  if (!isValid(date)) {
    console.warn("Invalid date:", iso);
    return "";
  }

  return format(date, "h:mma");
};

// Table cell style: "10:00am To 7:00pm"
const formatTableTimeRange = (
  startIso?: string | null,
  endIso?: string | null,
) =>
  `${formatClockTime(startIso).toLowerCase()} To ${formatClockTime(endIso).toLowerCase()}`;

// Bucket-button style: "10:00AM to 7:00PM"
const formatBracketLabel = (
  startIso?: string | null,
  endIso?: string | null,
) => {
  const start = formatClockTime(startIso);
  const end = formatClockTime(endIso);

  if (!start || !end) return "Invalid time";

  return `${start.toUpperCase()} to ${end.toUpperCase()}`;
};

// Groups records into the same shift bucket if they share the same
// (24-hour) start/end clock time, regardless of the actual date.
const bracketKeyOf = (record: any) => {
  const start = new Date(record.startDate);
  const end = new Date(record.endDate);

  if (!isValid(start) || !isValid(end)) {
    return "";
  }

  return `${format(start, "HH:mm")}-${format(end, "HH:mm")}`;
};

const isNightShiftRecord = (record: any) => {
  if (!record?.startDate || !record?.endDate) return false;

  const start = new Date(record.startDate);
  const end = new Date(record.endDate);

  if (!isValid(start) || !isValid(end)) return false;

  // End date is next day or later than start date
  return (
    end.getTime() > start.getTime() &&
    end.toDateString() !== start.toDateString()
  );
};

const resourceOerViewSchema = yup.object().shape({
  start_date: yup.date().required("This field is required"),
  end_date: yup.date().required("This field is required"),
  //Employeeshift timing
  start_date_emp_timing: yup.date().required("This field is required"),
});

const ResourceOveriew = () => {
  const navigate = useNavigate();
  const { userInfo, activeTab, setActiveTab,isAdmin } = useAuth();
  const agencyID = userInfo?.agencies?.id || "";

  const filterStartDate = localStorage.getItem("resourceOverviewStartDate");
  const filterEndDate = localStorage.getItem("resourceOverviewEndDate");

  const [searchQuery, setSearchQuery] = useState("");

  //Here states for Employeeshift timing
  const [searchQueryEmpTiming, setSearchQueryEmpTiming] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const [sortingNight, setSortingNight] = useState<SortingState>([]);
  const [selectedRowsNight, setSelectedRowsNight] = useState<Set<string>>(
    new Set(),
  );

  const [sortingLeaves, setSortingLeaves] = useState<SortingState>([]);
  const [selectedRowsLeaves, setSelectedRowsLeaves] = useState<Set<string>>(
    new Set(),
  );

  const [mainTable, setMainTable] = useState<any>(null);
  const [mainTableUpdate, setMainTableUpdate] = useState(false);

  const [mainTableNight, setMainTableNight] = useState<any>(null);
  const [mainTableUpdateNight, setMainTableUpdateNight] = useState(false);

  const [mainTableLeave, setMainTableLeave] = useState<any>(null);
  const [mainTableUpdateLeave, setMainTableUpdateLeave] = useState(false);

  const [activeBracket, setActiveBracket] = useState<string>("ALL");
  const [activeNightBracket, setActiveNightBracket] = useState<string>("ALL");
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  //api
  const [getResourceOverview, { data, loading: resourceOveriewDataLoading }] =
    useLazyQuery(GET_RESOURCE_OVERVIEW, {
      fetchPolicy: "no-cache",
    });

  const [
    getEmpTimingData,
    {
      data: empTimingData,
      loading: empTimingLoading,
      refetch: refetchEmpTimingData,
    },
  ] = useLazyQuery(GET_RESOURCE_WORKING_DATES, {
    fetchPolicy: "no-cache",
  });

  const [
  getEmployeeActiveStats,
  {
    data: employeeActiveStatsData,
    loading: employeeActiveStatsLoading,
  },
] = useLazyQuery(EMPLOYEE_ACTIVE_STATS_RESOURCES, {
  fetchPolicy: "no-cache",
});

  const [deleteResourceWorkingDates, { loading: deleting }] = useMutation(
    DELETE_RESOURCE_WORKING_DATES,
  );

  const [podOptions, setPodOptions] = useState([]);
  const [podOptionsSelected, setPodOptionsSelected] = useState(0);
  const [podOptionsSelectedLeaves, setPodOptionsSelectedLeaves] = useState(0);

  const [leavesDays, setLeavesDays] = useState<number>(14);

  const [getLeavesByPod, { data: leavesData, loading: leavesLoading }] =
    useLazyQuery(LEAVES_BY_POD, {
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    if (activeTab !== "Forward View Leaves") return;

    getLeavesByPod({
      variables: {
        current_date: format(new Date(), "yyyy-MM-dd'T'00:00:00"),
        number_of_days: leavesDays,
        pod_id: Number(podOptionsSelectedLeaves) || 0,
      },
    });
  }, [activeTab, leavesDays, podOptionsSelectedLeaves]);

  const { loading } = useQuery(PODS_DATA_LIST, {
    fetchPolicy: "no-cache",
    variables: {
      start_date: format(new Date(), "yyyy-MM-dd"),
      end_date: format(new Date(), "yyyy-MM-dd"),
      agency_id: Number(agencyID),
    },
    onCompleted: (da: any) => {
      if (da?.pods_data?.status_code === 200) {
        const data = da?.pods_data?.data?.map((i: any) => {
          return {
            key: i.pod_id,
            name: i.pod_name,
          };
        });
        setPodOptions(data);
      }
    },
  });

  const {
    setValue,
    formState: { errors },
    watch,
    trigger,
    control,
    getValues,
  } = useForm<any>({
    resolver: yupResolver(resourceOerViewSchema),
    defaultValues: {
      end_date: filterEndDate || "",
      start_date: filterStartDate || "",
      // Default to today so Employee Shift & Timing loads with today's data.
      start_date_emp_timing: format(new Date(), "yyyy-MM-dd"),
    },
  });

  //watch
  const watchStartDate = watch("start_date");
  const watchEndDate = watch("end_date");
  const watchStartDateEmpTiming = watch("start_date_emp_timing");

  useEffect(() => {
    if (agencyID && watchStartDate && watchEndDate) {
      const input = {
        agency_id: parseInt(agencyID),
        start_date: watchStartDate,
        end_date: watchEndDate,
      };

      getResourceOverview({
        variables: {
          input_data: input,
        },
      });
    }
  }, [agencyID, watchStartDate, watchEndDate]);


  useEffect(() => {
  const isAnyPopupOpen =
    deleteTarget 

  if (isAnyPopupOpen) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }

  return () => {
    document.body.style.overflow = "";
  };
}, [
deleteTarget
]);

  // Fetch Employee Shift & Timing data whenever the selected date changes.
  useEffect(() => {
    if (activeTab !== "Employee Shift & Timing") return;
    if (!watchStartDateEmpTiming) return;
    getEmpTimingData({
      variables: {
        start_date: toApiDateTime(watchStartDateEmpTiming),
        ...(podOptionsSelected !== 0 && { pod_id: podOptionsSelected }),
      },
    });

      // Active goals/tasks for employees
  getEmployeeActiveStats({
    variables: {
      target_date: toApiDateTime(watchStartDateEmpTiming),
    },
  });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchStartDateEmpTiming, podOptionsSelected, activeTab]);

  const resourceOverviewData = data?.get_resource_overview?.data;

  const employeeActiveStats =
  employeeActiveStatsData?.employee_active_stats?.records || [];

const employeeActiveStatsMap = useMemo(() => {
  const map = new Map<
    number,
    {
      goal_count: number;
      task_count: number;
    }
  >();

  employeeActiveStats.forEach((record: any) => {
    map.set(Number(record.employee_id), {
      goal_count: Number(record.active_goal_count) || 0,
      task_count: Number(record.active_task_count) || 0,
    });
  });

  return map;
}, [employeeActiveStats]);

  // Filter members based on search query (name, email, and skills)
  const filteredMembers =
    resourceOverviewData?.members
      ?.filter((member: any) => {
        if (!searchQuery) return true;

        const query = searchQuery.toLowerCase();

        const fullName =
          `${member?.first_name || ""} ${member?.last_name || ""}`.toLowerCase();

        const email = (member?.email || "").toLowerCase();

        const skills = (member?.skill_set || [])
          .map((skill: string) => skill.toLowerCase())
          .join(" ");

        return (
          fullName.includes(query) ||
          email.includes(query) ||
          skills.includes(query)
        );
      })
      ?.sort((a: any, b: any) => {
        const nameA =
          `${a?.first_name || ""} ${a?.last_name || ""}`.trim();

        const nameB =
          `${b?.first_name || ""} ${b?.last_name || ""}`.trim();

        return nameA.localeCompare(nameB, undefined, {
          sensitivity: "base",
        });
      }) || [];

  /**
   * ---- Employee Shift & Timing: derive brackets / day vs night ------------
   */

  const empTimingRecords =
    empTimingData?.resource_working_dates?.records?.map((record: any) => ({
      ...record,
      employeeId: record.employee_id,
      employeeName: record.employee_name,
      startDate: record.start_date,
      endDate: record.end_date,
    })) || [];

  const dayShiftRecords = useMemo(
    () => empTimingRecords.filter((r: any) => !isNightShiftRecord(r)),
    [empTimingRecords],
  );
  const nightShiftRecords = useMemo(
    () => empTimingRecords.filter((r: any) => isNightShiftRecord(r)),
    [empTimingRecords],
  );

  const brackets = useMemo(() => {
    const map = new Map<string, { key: string; label: string }>();

    dayShiftRecords.forEach((r: any) => {
      const key = bracketKeyOf(r);

      if (key && !map.has(key)) {
        map.set(key, {
          key,
          label: formatBracketLabel(r.startDate, r.endDate),
        });
      }
    });

    return [{ key: "ALL", label: "All" }, ...Array.from(map.values())];
  }, [dayShiftRecords]);

  const nightBrackets = useMemo(() => {
    const map = new Map<string, { key: string; label: string }>();

    nightShiftRecords.forEach((r: any) => {
      const key = bracketKeyOf(r);

      if (key && !map.has(key)) {
        map.set(key, {
          key,
          label: formatBracketLabel(r.startDate, r.endDate),
        });
      }
    });

    return [{ key: "ALL", label: "All" }, ...Array.from(map.values())];
  }, [nightShiftRecords]);

  // Keep the active bracket valid as data changes; default to the first one.
  useEffect(() => {
    if (brackets.length === 0) {
      setActiveBracket("ALL");
      return;
    }
    if (!brackets.some((b) => b.key === activeBracket)) {
      setActiveBracket(brackets[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brackets, activeBracket]);

  useEffect(() => {
    if (nightBrackets.length === 0) {
      setActiveNightBracket("ALL");
      return;
    }

    if (!nightBrackets.some((b) => b.key === activeNightBracket)) {
      setActiveNightBracket(nightBrackets[0].key);
    }
  }, [nightBrackets, activeNightBracket]);

  const matchesEmpSearch = (record: any) => {
    if (!searchQueryEmpTiming) return true;
    const q = searchQueryEmpTiming.toLowerCase();
    return (record.employeeName || "").toLowerCase().includes(q);
  };

  // Row shape consumed by DynamicTable/columns below.
const toRow = (record: any) => {
  const stats = employeeActiveStatsMap.get(Number(record.employeeId));

  return {
    id: record.id,
    employee_id: record.employeeId,
    employee_name:
      record.employeeName || `Employee #${record.employeeId}`,
    designation: record?.designation,
    pod_name: record?.pod_name,

    // Now coming from employee_active_stats API
    goal_count: stats?.goal_count ?? 0,
    task_count: stats?.task_count ?? 0,

    leave_status: record?.leave_status,
    start_time_end_time: formatTableTimeRange(
      record.startDate,
      record.endDate
    ),
    _record: record,
  };
};

const dayShiftTableData = useMemo(() => {
  if (!activeBracket) return [];

  return dayShiftRecords
    .filter(
      (r: any) =>
        activeBracket === "ALL" ||
        bracketKeyOf(r) === activeBracket
    )
    .filter(matchesEmpSearch)
    .map(toRow);
}, [
  dayShiftRecords,
  activeBracket,
  searchQueryEmpTiming,
  employeeActiveStatsMap,
]);

const nightShiftTableData = useMemo(() => {
  return nightShiftRecords
    .filter(
      (r: any) =>
        activeNightBracket === "ALL" ||
        bracketKeyOf(r) === activeNightBracket
    )
    .filter(matchesEmpSearch)
    .map(toRow);
}, [
  nightShiftRecords,
  activeNightBracket,
  searchQueryEmpTiming,
  employeeActiveStatsMap,
]);

  /**
   * ---- Employee Shift & Timing: edit / delete handlers ---------------------
   */

  const handleEditRow = (row: any) => {
    navigate("/add_timing", {
      state: {
        record: {
          id: row._record.id,
          employee_id: row._record.employeeId,
          start_date: row._record.startDate,
          end_date: row._record.endDate,
          notes: row._record.notes,
        },
      },
    });
  };

  const handleDeleteRowClick = (row: any) => setDeleteTarget(row);

  const confirmDeleteRow = async () => {
    if (!deleteTarget) return;
    try {
      const res = await deleteResourceWorkingDates({
        variables: { ids: [Number(deleteTarget.id)] },
      });
      const result = res?.data?.delete_resource_working_dates;
      if (result?.status === "Success") {
        showSuccess?.("Record deleted successfully.");
        refetchEmpTimingData?.();
      } else {
        showError("Failed to delete record.");
      }
    } catch (err: any) {
      showError("Something went wrong while deleting.");
    } finally {
      setDeleteTarget(null);
    }
  };

const getInitials = (name: string) => {
  return name
    ?.trim()
    ?.split(/\s+/)
    ?.slice(0, 2)
    ?.map((word) => word.charAt(0).toUpperCase())
    ?.join("");
};

  const columns = [
    {
      key: "employee_name",
      name: "Employee Name",
      id: "employee_name",
      header: "Employee Name",
      accessorKey: "employee_name",
      enableSorting: true,
      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        const valueA = String(rowA.getValue(columnId) || "").toLowerCase();
        const valueB = String(rowB.getValue(columnId) || "").toLowerCase();
        return valueA.localeCompare(valueB);
      },
      cell: ({ row }: any) => {
        const empName = row.original.employee_name;

        return (
          <div className="flex items-center gap-[8px] theme_btn_item">
            <span className="emp-profile-blg-dv">{getInitials(empName)}</span>
            <span>{empName}</span>
          </div>
        );
      },
    },
    {
      key: "designation",
      name: "Department",
      id: "designation",
      header: "Department",
      accessorKey: "designation",
      enableSorting: true,
      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        const valueA = String(rowA.getValue(columnId) || "").toLowerCase();
        const valueB = String(rowB.getValue(columnId) || "").toLowerCase();
        return valueA.localeCompare(valueB);
      },
    },

    {
      key: "pod_name",
      name: "Pod",
      id: "pod_name",
      header: "Pod",
      accessorKey: "pod_name",
      enableSorting: true,

  filterFn: (row: any, columnId: string, filterValue: string) => {
    const value = String(row.getValue(columnId) || "").toLowerCase();
    const search = String(filterValue || "").toLowerCase();

    return value.includes(search);
  },
    },
    {
      key: "start_time_end_time",
      name: "Start Time/End Time",
      id: "start_time_end_time",
      header: "Start Time/End Time",
      accessorKey: "start_time_end_time",
      enableSorting: true,
      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        const valueA = String(rowA.getValue(columnId) || "").toLowerCase();
        const valueB = String(rowB.getValue(columnId) || "").toLowerCase();
        return valueA.localeCompare(valueB);
      },
      cell: ({ row }: any) => {
        const leavestatus = row.original.leave_status;
        const start_time_end_time = row.original.start_time_end_time;

        return (
          <div className="flow items-center gap-[4px] theme_btn_item">
            <div className="relative top-[-6px]">{start_time_end_time}</div>
            {leavestatus == "On leave" ? (
              <span className="onleave-status-emp min-w-max">On Leave </span>
            ) : (
              <span className="avilable-status-emp min-w-max">Available </span>
            )}
          </div>
        );
      },
    },
    {
      key: "goal_count",
      name: "Open Goals",
      id: "goal_count",
      header: "Open Goals",
      accessorKey: "goal_count",
      enableSorting: true,
    },
    {
      key: "task_count",
      name: "Open Tasks",
      id: "task_count",
      header: "Open Tasks",
      accessorKey: "task_count",
      enableSorting: true,
    },

     ...(isAdmin
    ? [
        {
          key: "action",
          name: "Action",
          id: "action",
          header: "Action",
          accessorKey: "action",
          hideSearch: true,
          cell: ({ row }: any) => {
            const original = row.original;

            return (
              <div className="flex items-center gap-[8px] theme_btn_item">
                <button
              type="button"
              title="Edit"
              onClick={() => handleEditRow(original)}
              className="flex items-center justify-center w-8 h-8 rounded-full border cursor-pointer border-surface-border hover:border-surface-primary-surface"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <g clip-path="url(#clip0_43335_97583)">
                  <path
                    d="M14.1866 4.26678L7.8266 10.6267C7.19327 11.2601 5.31325 11.5534 4.89325 11.1334C4.47325 10.7134 4.75991 8.83342 5.39325 8.20008L11.7599 1.83343C11.9169 1.66214 12.107 1.52444 12.3187 1.42864C12.5304 1.33284 12.7593 1.28091 12.9917 1.27602C13.2239 1.27113 13.4549 1.31336 13.6704 1.40016C13.8859 1.48697 14.0817 1.61657 14.2457 1.7811C14.4098 1.94564 14.5389 2.1417 14.6251 2.35748C14.7113 2.57326 14.7529 2.80426 14.7474 3.03656C14.7419 3.26886 14.6893 3.49765 14.5929 3.70908C14.4965 3.92051 14.3583 4.11024 14.1866 4.26678Z"
                    stroke="black"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M7.33398 2.66675H4.00065C3.2934 2.66675 2.61517 2.94769 2.11507 3.44779C1.61498 3.94789 1.33398 4.62617 1.33398 5.33341V12.0001C1.33398 12.7073 1.61498 13.3856 2.11507 13.8857C2.61517 14.3858 3.2934 14.6667 4.00065 14.6667H11.334C12.8073 14.6667 13.334 13.4667 13.334 12.0001V8.66675"
                    stroke="black"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_43335_97583">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>
            <button
              type="button"
              title="Delete"
              onClick={() => handleDeleteRowClick(original)}
              className="flex items-center justify-center w-8 h-8 rounded-full border cursor-pointer border-surface-border hover:border-red-400 text-red-500"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M6.5 1H9.5C9.77614 1 10 1.22386 10 1.5V2.5H6V1.5C6 1.22386 6.22386 1 6.5 1ZM11 2.5V1.5C11 0.671573 10.3284 0 9.5 0H6.5C5.67157 0 5 0.671573 5 1.5V2.5H2.50566C2.50226 2.49997 2.49885 2.49997 2.49544 2.5H1.5C1.22386 2.5 1 2.72386 1 3C1 3.27614 1.22386 3.5 1.5 3.5H2.0384L2.89116 14.1595C2.97431 15.1989 3.84207 16 4.88479 16H11.1152C12.1579 16 13.0257 15.1989 13.1088 14.1595L13.9616 3.5H14.5C14.7761 3.5 15 3.27614 15 3C15 2.72386 14.7761 2.5 14.5 2.5H13.5046C13.5011 2.49997 13.4977 2.49997 13.4943 2.5H11ZM12.9584 3.5L12.112 14.0797C12.0704 14.5994 11.6366 15 11.1152 15H4.88479C4.36343 15 3.92955 14.5994 3.88798 14.0797L3.0416 3.5H12.9584ZM5.47064 4.50086C5.74631 4.48465 5.98292 4.69497 5.99914 4.97064L6.49914 13.4706C6.51535 13.7463 6.30503 13.9829 6.02936 13.9991C5.7537 14.0154 5.51708 13.805 5.50086 13.5294L5.00086 5.02936C4.98465 4.7537 5.19497 4.51708 5.47064 4.50086ZM10.5294 4.50086C10.805 4.51708 11.0154 4.7537 10.9991 5.02936L10.4991 13.5294C10.4829 13.805 10.2463 14.0154 9.97064 13.9991C9.69497 13.9829 9.48465 13.7463 9.50086 13.4706L10.0009 4.97064C10.0171 4.69497 10.2537 4.48465 10.5294 4.50086ZM8 4.5C8.27614 4.5 8.5 4.72386 8.5 5V13.5C8.5 13.7761 8.27614 14 8 14C7.72386 14 7.5 13.7761 7.5 13.5V5C7.5 4.72386 7.72386 4.5 8 4.5Z"
                  fill="#2B2B2B"
                />
              </svg>
            </button>
              </div>
            );
          },
        },
      ]
    : []),
  ];

  const leavesRecords = leavesData?.leaves_by_pod?.records || [];

  const leavesTableData = useMemo(
    () =>
      leavesRecords.map((r: any) => ({
        id: r.id,
        employee_name: r.employee_name,
        pod_name: r.pod_name,
        leave_type: r.leave_type,
        from_to: r.from_to,
        status: r.status,
        reason: r.reason || "-",
        day_type: r.is_half_day ? "Half Day" : "Full Day",
        _record: r,
      })),
    [leavesRecords],
  );

  const columnsNight = [
    {
      key: "employee_name",
      name: "Employee Name",
      id: "employee_name",
      header: "Employee Name",
      accessorKey: "employee_name",
      enableSorting: true,
      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        const valueA = String(rowA.getValue(columnId) || "").toLowerCase();
        const valueB = String(rowB.getValue(columnId) || "").toLowerCase();
        return valueA.localeCompare(valueB);
      },
      cell: ({ row }: any) => {
        const empName = row.original.employee_name;

        return (
          <div className="flex items-center gap-[8px] theme_btn_item">
            <span className="emp-profile-blg-dv">{getInitials(empName)}</span>
            <span>{empName}</span>
          </div>
        );
      },
    },
    {
      key: "pod_name",
      name: "Pod",
      id: "pod_name",
      header: "Pod",
      accessorKey: "pod_name",
      enableSorting: true,
    },
    {
      key: "leave_type",
      name: "Leave Type",
      id: "leave_type",
      header: "Leave Type",
      accessorKey: "leave_type",
      enableSorting: true,
      cell: ({ row }: any) => {
        const leave_type = row.original.leave_type;
        return (
          <div className="flex items-center gap-[8px] theme_btn_item">
            {leave_type == "Casual Leave" && <span>{leave_type}</span>}
            {leave_type == "Sick Leave" && <span>{leave_type}</span>}
            {leave_type == "Emergency Leave" && <span>{leave_type}</span>}
            {leave_type == "Paternity Leave" && <span>{leave_type}</span>}
            {leave_type == "Maternity Leave" && <span>{leave_type}</span>}
          </div>
        );
      },
    },
    {
      key: "from_to",
      name: "From - To",
      id: "from_to",
      header: "From - To",
      accessorKey: "from_to",
      enableSorting: true,
      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        const valueA = String(rowA.getValue(columnId) || "").toLowerCase();
        const valueB = String(rowB.getValue(columnId) || "").toLowerCase();
        return valueA.localeCompare(valueB);
      },
    },
    {
      key: "reason",
      name: "Reason",
      id: "reason",
      header: "Reason",
      accessorKey: "reason",
      enableSorting: true,
    },
    {
      key: "day_type",
      name: "Half Day/Full Day",
      id: "day_type",
      header: "Half Day/Full Day",
      accessorKey: "day_type",
      enableSorting: true,
    },
    {
      key: "status",
      name: "Status",
      id: "status",
      header: "Status",
      accessorKey: "status",
      enableSorting: true,
      cell: ({ row }: any) => {
        const leave_status = row.original.status;
        return (
          <div className="flex items-center gap-[8px] theme_btn_item">
            {leave_status == "Approved" && <span className="avilable-status-emp">{leave_status}</span>}
            {leave_status == "Pending" && <span className="casualleave-status">{leave_status}</span>}
            {leave_status == "Rejected" && <span className="onleave-status-emp">{leave_status}</span>}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <form
        id="cts-respon-p-dv"
        className="w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope"
      >
        <div className="w-full flex mq991:contains flex-col items-start justify-start gap-6">
          <BreadCumb />
          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => navigate(-1)}
            />
            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
              Resource Overview
            </h3>
          </div>
          <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
            <div className="self-stretch flex flex-row flex-wrap items-center gap-[6px]   pb-1">
              {[
                "Overview",
                "Employee Shift & Timing",
                "Forward View Leaves",
              ].map((status) => {
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => {
                      setActiveTab(status);
                    }}
                    className={`cursor-pointer text-sm  px-3 py-1 no-underline rounded-full border transition-colors ${activeTab === status
                        ? "relative link-filterSvgrepo-dv cursor-pointer inactive_item_blgs active_item_blgs"
                        : "relative link-filterSvgrepo-dv cursor-pointer inactive_item_blgs "
                      }`}
                  >
                    {status}
                  </button>
                );
              })}
            </div>

            {activeTab == "Overview" && (
              <>
                <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                  <div className="status-filter pb-4 grid gap-[8px]">
                    <Controller
                      name="start_date"
                      control={control}
                      render={({ field }) => (
                        <DateSelector
                          label="Start Date*"
                          selectedDate={field.value}
                          onChange={(date: string | any) => {
                            const endDate = watch("end_date");
                            const result = true;
                            if (result) {
                              field.onChange(date);
                              setValue("end_date", null);
                              setValue("start_date", date);
                              trigger("start_date");
                              localStorage.setItem(
                                "resourceOverviewStartDate",
                                date,
                              );
                            }
                          }}
                          nothideParsedValue={true}
                          blueIcon={true}
                          error={errors.start_date?.message}
                          showAllDate={true}
                        />
                      )}
                    />
                  </div>
                  <div className="status-filter pb-4 grid gap-[8px]">
                    <Controller
                      name="end_date"
                      control={control}
                      render={({ field }) => (
                        <DateSelector
                          label="End Date*"
                          selectedDate={field.value}
                          onChange={(date: string | null | any) => {
                            const date2 = parseISO(watch("start_date"));
                            const date1 = parseISO(date);
                            if (isBefore(date1, date2)) {
                              showError(
                                "Please select date greater than start date.",
                              );
                            } else if (differenceInDays(date1, date2) > 7) {
                              showError(
                                "The selected date range cannot exceed 7 days.",
                              );
                            } else {
                              field.onChange(date);
                              setValue("end_date", date);
                              trigger("end_date");
                              localStorage.setItem(
                                "resourceOverviewEndDate",
                                date,
                              );
                            }
                          }}
                          nothideParsedValue={true}
                          blueIcon={true}
                          dontopenDatepicker={!watch("start_date")}
                          error={errors.end_date?.message}
                          showAllDate={true}
                        />
                      )}
                    />
                  </div>
                  <div className="status-filter pb-4 grid gap-[8px]">
                    <InputsNew
                      label="Search Members"
                      type="text"
                      contentPlaceholder="Search by name, email or skills..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="w-full relative flex flex-col items-start p-num-16 box-border text-left text-num-18 text-gray font-manrope">
                  <div className="self-stretch flex flex-col items-start gap-6">
                    {/* Header */}
                    <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-1 mq575:grid-cols-1 mt-[8px]  min-w-full self-stretch flex-row items-start justify-start gap-[8px] mq991:gap-[8px] text-lg">
                      <div className="flex-1 flex flex-col items-center resource_box_item lightblue-item p-4 ">
                        <div className="self-stretch rounded-num-20   overflow-hidden shrink-0 flex flex-col items-start gap-2">
                          <div className="self-stretch flex items-center gap-2">
                            <img
                              className="w-6 relative max-h-full"
                              src={resouceGroupicon}
                              alt=""
                            />
                            <h5 className="flex-1 flex items-center my-0">
                              <b className="relative">Team Members</b>
                            </h5>
                          </div>
                          <div className="self-stretch rounded-num-20 flex items-center gap-4 text-num-42 inner_resource_box_item p-4">
                            <div className="flex-1 flex flex-col items-start justify-center p-num-16 gap-2 inner_resource_box">
                              <h1>
                                <b className="relative">
                                  {resourceOverviewData &&
                                    resourceOverviewData?.available_members_count}
                                </b>
                              </h1>
                              <div className="relative text-num-16 font-semibold">
                                Available Members
                              </div>
                            </div>
                            <div className="self-stretch w-px relative bg-whitesmoke-200" />
                            <div className="flex-1 flex flex-col items-start justify-center p-num-16 gap-2 text-dimgray-200 inner_resource_box">
                              <h1>
                                <b className="relative">
                                  {resourceOverviewData &&
                                    resourceOverviewData?.total_members_count}
                                </b>
                              </h1>
                              <div className="self-stretch relative text-num-16 font-semibold">
                                Total Members
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 rounded-num-20 bg-white p-4 resource_box_item overflow-hidden flex flex-col items-start justify-center gap-2">
                        <div className="self-stretch flex items-center gap-2">
                          <img
                            className="w-6 relative h-6"
                            src={resourceClock}
                            alt=""
                          />
                          <h5 className="flex-1 flex items-center my-0">
                            <b className="relative">Capacity</b>
                          </h5>
                        </div>
                        <div className="self-stretch rounded-num-20  flex items-center gap-4 inner_resource_box_item p-4">
                          <div className="flex-1 flex flex-col items-start justify-center p-num-16 gap-1 inner_resource_box">
                            <h1>
                              <b className="relative">
                                {resourceOverviewData &&
                                  formatHoursToHM(
                                    resourceOverviewData?.allocated_working_hours,
                                  )}
                              </b>
                            </h1>

                            <div className="relative text-num-16 text-dimgray-200">
                              Used Bandwidth
                            </div>
                          </div>
                          <div className="flex-1 flex flex-col items-start justify-center p-num-16 gap-1 inner_resource_box">
                            <h1>
                              <b className="relative">
                                {resourceOverviewData &&
                                  formatHoursToHM(
                                    resourceOverviewData?.total_working_hours,
                                  )}
                              </b>
                            </h1>
                            <div className="self-stretch relative text-num-16 text-dimgray-200">
                              Capacity Bandwidth
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-1 mq575:grid-cols-1 mt-[8px]  min-w-full self-stretch flex-row items-start justify-start gap-[8px] mq991:gap-[8px] text-lg">
                      {filteredMembers?.length > 0 ? (
                        filteredMembers?.map((member: any) => {
                          return (
                            <TeamMemberCard
                              key={member?.id}
                              member={member}
                              startDate={getValues("start_date")}
                              endDate={getValues("end_date")}
                            />
                          );
                        })
                      ) : (
                        <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                          {searchQuery
                            ? "No members found matching your search."
                            : "No members available."}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTab == "Employee Shift & Timing" && (
              <>
                <p className="self-stretch relative text-sm text-dimgray-200 dark:text-gray-300">
                  View and manage employee schedules, availability and pod
                  information.
                </p>
                <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                  <div className="status-filter pb-4 grid gap-[8px]">
                    <Controller
                      name="start_date_emp_timing"
                      control={control}
                      render={({ field }) => (
                        <DateSelector
                          label="Date*"
                          selectedDate={field.value}
                          onChange={(date: string | any) => {
                            field.onChange(date);
                            setValue("start_date_emp_timing", date);
                            trigger("start_date_emp_timing");
                          }}
                          nothideParsedValue={true}
                          blueIcon={true}
                          error={errors.start_date_emp_timing?.message}
                          showAllDate={true}
                        />
                      )}
                    />
                  </div>
                  <div className="status-filter pb-4 grid gap-[8px]">
                    <InputsNew
                      label="Search Employee"
                      type="text"
                      contentPlaceholder="Search by name"
                      value={searchQueryEmpTiming}
                      onChange={(e) => setSearchQueryEmpTiming(e.target.value)}
                    />
                  </div>
                  <div className="min-w-full">
                    <SearchableInputsDropdown
                      label="Pod"
                      placeHolder="All Pod"
                      options={podOptions}
                      onSelect={(key: any, name: any) => {
                        setPodOptionsSelected(key);
                      }}
                      dontselectlastValue={true}
                    />
                  </div>
                </div>

                {/* ---- Day shift: time bucket tabs + "+" add button ---- */}
                <div className="self-stretch flex flex-row items-center justify-between flex-wrap gap-3 mb-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-semibold dark:text-white">
                      Employee Scheduling Time
                    </span>

                    {brackets.map((b) => (
                      <button
                        key={b.key}
                        type="button"
                        onClick={() => setActiveBracket(b.key)}
                        className={`cursor-pointer text-sm font-bold px-3 py-2 rounded-md border transition-colors ${activeBracket === b.key
                            ? "bg-surface-primary-surface text-white border-surface-primary-surface-active-dv"
                            : "bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-surface-border bggarynoactive-dv"
                          }`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                  {
                    isAdmin &&   <button
                    type="button"
                    title="Add Member"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/add_timing");
                    }}
                    className=" cursor-pointer flex flex-col items-start justify-start p-[10px] plus-task-btn taskdash-plush-img rounded "
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M8 3.5V12.5"
                        stroke="#fff"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12.5 8H3.5"
                        stroke="#fff"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                  }
                
                </div>

                <div className="w-full relative grid flex-col items-start p-num-16 box-border text-left text-num-18 text-gray font-manrope emp-tbl-overview-dv ">
                  {brackets.length === 0 ? (
                    <div className="w-full text-center py-8 text-gray-500 dark:text-gray-400">
                      No day-shift records for this date.
                    </div>
                  ) : (
                    <DynamicTable
                      filteredData={dayShiftTableData}
                      selectedColumns={columns}
                      sorting={sorting}
                      setSorting={setSorting}
                      selectedRows={selectedRows}
                      setSelectedRows={setSelectedRows}
                      setMainTable={setMainTable}
                      setMainTableUpdate={setMainTableUpdate}
                      serialNumber={true}
                      currentPage={1}
                      notFoundBodyClass="no-data-found-day"
                    />
                  )}
                </div>

                {/* ---- Night shift (start time 2 PM or later) ---- */}
                <div className="self-stretch mt-4">
                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    <span className="text-sm font-semibold dark:text-white">
                      Employee Scheduling Over Night Shift
                    </span>

                    {nightBrackets.map((b) => (
                      <button
                        key={b.key}
                        type="button"
                        onClick={() => setActiveNightBracket(b.key)}
                        className={`cursor-pointer text-sm font-bold px-3 py-2 rounded-md border transition-colors ${activeNightBracket === b.key
                            ? "bg-surface-primary-surface text-white border-surface-primary-surface-active-dv"
                            : "bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-surface-border bggarynoactive-dv"
                          }`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                  <div className="w-full relative grid flex-col items-start p-num-16 box-border text-left text-num-18 text-gray font-manrope emp-tbl-overview-dv ">
                    {nightShiftTableData.length === 0 ? (
                      <div className="w-full text-center py-8 text-gray-500 dark:text-gray-400  nightshift-tbl-main">
                        No night-shift records for this date.
                      </div>
                    ) : (
                      <DynamicTable
                        filteredData={nightShiftTableData}
                        selectedColumns={columns}
                        sorting={sortingNight}
                        setSorting={setSortingNight}
                        selectedRows={selectedRowsNight}
                        setSelectedRows={setSelectedRowsNight}
                        setMainTable={setMainTableNight}
                        setMainTableUpdate={setMainTableUpdateNight}
                        serialNumber={true}
                        currentPage={1}
                        notFoundBodyClass="no-data-found-night"
                      />
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab == "Forward View Leaves" && (
              <>
                <p className="self-stretch relative text-sm text-dimgray-200 dark:text-gray-300 my-0">
                  View upcoming 14 and 30 days leaves schedule, grouped by pod.
                  Leave details include type, status and approver
                </p>
                <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                  <div className="min-w-full">
                    <SearchableInputsDropdown
                      label="Pod"
                      placeHolder="All Pod"
                      options={podOptions}
                      onSelect={(key: any, name: any) => {
                        setPodOptionsSelectedLeaves(Number(key) || 0);
                      }}
                      dontselectlastValue={true}
                    />
                  </div>
                  <div className="grid  items-center gap-2">
                    <label
                      className="self-stretch relative font-semibold pb-2 dark:text-white"
                      data-qb-rot="067cc17b-0c05-4513-9f85-b6647456a8f6"
                      data-qb-rot-theme="green"
                    >
                      View Period
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setLeavesDays(14)}
                        className={`cursor-pointer text-sm  px-3 py-2 rounded-md border transition-colors ${leavesDays === 14
                            ? "cursor-pointer text-sm  px-3 py-1 no-underline rounded-full border transition-colors relative link-filterSvgrepo-dv inactive_item_blgs active_item_blgs"
                            : "cursor-pointer text-sm  px-3 py-1 no-underline rounded-full border transition-colors relative link-filterSvgrepo-dv inactive_item_blgs "
                          }`}
                      >
                        Next 14 days
                      </button>
                      <button
                        type="button"
                        onClick={() => setLeavesDays(30)}
                        className={`cursor-pointer text-sm  px-3 py-2 rounded-md border transition-colors ${leavesDays === 30
                            ? "cursor-pointer text-sm  px-3 py-1 no-underline rounded-full border transition-colors relative link-filterSvgrepo-dv inactive_item_blgs active_item_blgs"
                            : "cursor-pointer text-sm  px-3 py-1 no-underline rounded-full border transition-colors relative link-filterSvgrepo-dv inactive_item_blgs"
                          }`}
                      >
                        Next 30 days
                      </button>
                    </div>
                  </div>
                </div>
                <div className="self-stretch flex flex-row items-center justify-between flex-wrap gap-3 mb-3">
                  <div className="w-full relative grid flex-col items-start p-num-16 box-border text-left text-num-18 text-gray font-manrope emp-tbl-overview-dv  leaveforward-tbl-dv ">
                    {leavesTableData.length === 0 ? (
                      <div className="w-full text-center py-8 text-gray-500 dark:text-gray-400">
                        No leave records found.
                      </div>
                    ) : (
                      <DynamicTable
                        filteredData={leavesTableData}
                        selectedColumns={columnsNight}
                        sorting={sortingLeaves}
                        setSorting={setSortingLeaves}
                        selectedRows={selectedRowsLeaves}
                        setSelectedRows={setSelectedRowsLeaves}
                        setMainTable={setMainTableLeave}
                        setMainTableUpdate={setMainTableUpdateLeave}
                        serialNumber={true}
                        currentPage={1}
                        notFoundBodyClass="no-data-found-leaves"
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </form>

      {/* ---- Delete confirmation popup ---- */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-slate-800 rounded-radius-md p-6 w-full max-w-sm shadow-lg">
            <div className="flex items-center justify-between pb-0 border-b border-gray-100 dark:border-gray-700">
              <h4 className="text-base font-semibold my-0 dark:text-white">
                Delete this record?
              </h4>
              <button  onClick={() => setDeleteTarget(null)} className="text-gray-400 cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1" aria-label="Close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <p className="text-sm text-dimgray-200 dark:text-gray-300 mb-6">
              You are about to delete the shift for {deleteTarget.employee_name}{" "}
              ({deleteTarget.start_time_end_time}). This action cannot be
              undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={confirmDeleteRow}
                className="py-2 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] rounded-radius-md bg-red-600 text-white text-sm font-semibold disabled:opacity-60 cta-red"
              >
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

    {(resourceOveriewDataLoading ||
  empTimingLoading ||
  employeeActiveStatsLoading ||
  loading ||
  leavesLoading) && <Loader />}
    </>
  );
};

export default ResourceOveriew;
